/**
 * Demo Users API Service
 * 
 * Handles fetching and managing demo users for the interactive demo.
 */

import api from './api/api';

export interface DemoUser {
  id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  is_active: boolean;
  is_superuser: boolean;
  roles?: string[];
  created_at: string;
  updated_at: string;
}

export interface DemoUserResponse {
  users: DemoUser[];
}

export class DemoUsersAPI {
  private static baseUrl = '/dev/users';

  /**
   * Get all demo users
   */
  static async getDemoUsers(): Promise<DemoUser[]> {
    try {
      const response = await api.get(`${this.baseUrl}/`);
      const users = response.data;
      
      // Filter for demo users (users with 'demo' role or demo-specific usernames)
      const demoUsers = users.filter((user: any) => 
        user.username?.includes('demo') || 
        user.username?.includes('alex_johnson') ||
        user.username?.includes('sarah_chen') ||
        user.username?.includes('mike_rodriguez') ||
        user.roles?.includes('demo')
      );

      // Transform to include full_name
      return demoUsers.map((user: any) => ({
        ...user,
        full_name: user.first_name && user.last_name 
          ? `${user.first_name} ${user.last_name}` 
          : user.username
      }));
    } catch (error) {
      console.error('Failed to fetch demo users:', error);
      // Return fallback demo users if API fails (using actual UUIDs from database)
      return [
        {
          id: 'b6dd68c7-28ab-4d1b-b7fd-4793692f911c',
          username: 'demo_user',
          email: 'demo@krysalis.com',
          first_name: 'Demo',
          last_name: 'User',
          full_name: 'Demo User',
          is_active: true,
          is_superuser: false,
          roles: ['demo'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '5971126b-1043-4de0-8a95-1b26b7c6db88',
          username: 'alex_johnson',
          email: 'alex.johnson@krysalis.com',
          first_name: 'Alex',
          last_name: 'Johnson',
          full_name: 'Alex Johnson',
          is_active: true,
          is_superuser: false,
          roles: ['demo', 'operator'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: 'c7e4c468-e6e4-4fd1-85d4-acda5a09ae42',
          username: 'sarah_chen',
          email: 'sarah.chen@krysalis.com',
          first_name: 'Sarah',
          last_name: 'Chen',
          full_name: 'Sarah Chen',
          is_active: true,
          is_superuser: false,
          roles: ['demo', 'coach'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        },
        {
          id: '601edc72-3b1d-4ae2-83cd-9356661fb16f',
          username: 'mike_rodriguez',
          email: 'mike.rodriguez@krysalis.com',
          first_name: 'Mike',
          last_name: 'Rodriguez',
          full_name: 'Mike Rodriguez',
          is_active: true,
          is_superuser: false,
          roles: ['demo', 'architect'],
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        }
      ];
    }
  }

  /**
   * Get a specific demo user by ID
   */
  static async getDemoUser(userId: string): Promise<DemoUser | null> {
    try {
      const response = await api.get(`${this.baseUrl}/${userId}`);
      const user = response.data;
      
      return {
        ...user,
        full_name: user.first_name && user.last_name 
          ? `${user.first_name} ${user.last_name}` 
          : user.username
      };
    } catch (error) {
      console.error('Failed to fetch demo user:', error);
      return null;
    }
  }
}

export default DemoUsersAPI;
